import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { I18nProvider } from "@/lib/i18n";
import { AuthProvider, useAuth } from "@/lib/auth";
import { CartProvider } from "@/lib/cart";

import PublicLayout from "@/components/layout/PublicLayout";
import AdminLayout from "@/components/layout/AdminLayout";

import Home from "@/pages/public/Home";
import About from "@/pages/public/About";
import Menu from "@/pages/public/Menu";
import { Shop, ProductDetail } from "@/pages/public/Shop";
import { Cart, Checkout } from "@/pages/public/Cart";
import Events from "@/pages/public/Events";
import ReservationStatus from "@/pages/public/ReservationStatus";
import Contact from "@/pages/public/Contact";

import AuthCallback from "@/pages/AuthCallback";
import AdminLogin from "@/pages/admin/AdminLogin";
import Dashboard from "@/pages/admin/Dashboard";
import Orders from "@/pages/admin/Orders";
import EventsCRM from "@/pages/admin/EventsCRM";
import Customers from "@/pages/admin/Customers";
import MenuManagement from "@/pages/admin/MenuManagement";
import ProductManagement from "@/pages/admin/ProductManagement";
import UsersManagement from "@/pages/admin/UsersManagement";

function ProtectedAdmin({ children }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loc.state?.user && ["owner", "admin", "staff"].includes(loc.state.user.role)) return children;
  if (loading) return <div className="min-h-screen flex items-center justify-center label-eyebrow text-[hsl(var(--sk-fg))]/60">Loading…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;
  if (!["owner", "admin", "staff"].includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  // CRITICAL: Detect session_id in URL fragment (Emergent OAuth callback)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<PublicLayout />}>
        <Route index element={<Home />} />
        <Route path="about" element={<About />} />
        <Route path="menu" element={<Menu />} />
        <Route path="shop" element={<Shop />} />
        <Route path="shop/:id" element={<ProductDetail />} />
        <Route path="cart" element={<Cart />} />
        <Route path="checkout" element={<Checkout />} />
        <Route path="events" element={<Events />} />
        <Route path="events/status" element={<ReservationStatus />} />
        <Route path="events/status/:booking_number" element={<ReservationStatus />} />
        <Route path="contact" element={<Contact />} />
      </Route>
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin" element={<ProtectedAdmin><AdminLayout /></ProtectedAdmin>}>
        <Route index element={<Dashboard />} />
        <Route path="orders" element={<Orders />} />
        <Route path="events" element={<EventsCRM />} />
        <Route path="customers" element={<Customers />} />
        <Route path="menu" element={<MenuManagement />} />
        <Route path="products" element={<ProductManagement />} />
        <Route path="users" element={<UsersManagement />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <CartProvider>
        <BrowserRouter basename="/demo-growithm.id">
        <AppRouter />
        <Toaster position="top-center" richColors />
        </BrowserRouter>
        </CartProvider>
      </AuthProvider>
    </I18nProvider>
  );
}

export default App;
